import React, { useState } from "react"
import * as yup from "yup"
import { Box, Typography } from "src/UILibrary"
import { useTranslation } from "react-i18next"
import { useMediaQuery } from "react-responsive"
import { validateField } from "src/modules/validation"
import InputTextField from "src/components/atom/InputTextField"
import "suneditor/dist/css/suneditor.min.css"
import { Button, Grid } from "@mui/material"
import FileSelectField from "src/components/atom/FileSelectField"
import { Book, BookLibraryService } from "src/openapi"
import { useNavigate } from "react-router"
import { useRecoilState } from "recoil"
import { ShowToast } from "src/states/showToast"
import { RESPONSE_CODE, SNACKBAR_TYPES } from "src/modules/const"
import { useLoginContext } from "src/hooks/useLoginContext"
import { BooksTableHeaderItem } from "./BooksTableHeaderItem"
import { BooksTableItem } from "./BooksTableItem"
import { ShowProgress } from "src/states/showProgress"

interface Props {
  edit?: boolean
  item?: Book
  closeDetail?: any
}
export const CreateBooksPageItem = ({ edit, item, closeDetail }: Props) => {
  const { t } = useTranslation("translation", { keyPrefix: "mypage.books" })
  const { checkLoginStatus, accessToken } = useLoginContext()
  const isPC = useMediaQuery({ query: "(min-width: 1400px)" })
  const isTablet = useMediaQuery({ query: "(min-width: 1200px)" })

  const [, setShowProgress] = useRecoilState(ShowProgress)

  const [title, setTitle] = useState<string>(edit ? item?.title! : "")
  const [url, setUrl] = useState<string>(edit ? item?.bookUrl! : "")
  const [titleValidationMessage, setTitleValidationMessage] = useState<string>("")
  const [urlValidationMessage, setUrlValidationMessage] = useState<string>("")

  const [image, setImage] = useState<string>(edit ? item?.imageUrl! : "")
  const [imageValidationMessage, setImageValidationMessage] = useState<string>("")

  const navigate = useNavigate()

  const [, setShowToast] = useRecoilState(ShowToast)

  const onFileSelected = (url: string) => {
    setImage(url)
  }

  const validate = async () => {
    let ret = true

    if (image) {
      setImageValidationMessage("")
    } else {
      setImageValidationMessage(t("please_select_file"))
    }

    try {
      await validateField(
        yup.object().shape({
          title: yup.string().required(t("please_input_titile")),
        }),
        "title",
        title
      )
      setTitleValidationMessage("")
    } catch (err: any) {
      setTitleValidationMessage(err.message)
      ret = ret && false
    }

    try {
      await validateField(
        yup.object().shape({
          url: yup.string().required(t("please_input_url")),
        }),
        "url",
        url
      )
      setUrlValidationMessage("")
    } catch (err: any) {
      setUrlValidationMessage(err.message)
      ret = ret && false
    }

    return ret
  }

  const handleBack = () => {
    navigate("/mypage/books/list")
    navigate(0)
  }

  const handleSave = async () => {
    if (!(await validate())) {
      return
    }

    if (edit) {
      setShowProgress(true)
      BookLibraryService.updateBook(accessToken, item?.id!, {
        title: title,
        imageUrl: image,
        bookUrl: url,
        isPublic: true,
      })
        .then((res) => {
          setShowProgress(false)
          if (!checkLoginStatus(res)) {
            return
          }

          if (res.status === RESPONSE_CODE.SUCCESS) {
            setShowToast({ show: true, text: t("success"), type: SNACKBAR_TYPES.SUCCESS })
          } else {
            setShowToast({ show: true, text: t("failed"), type: SNACKBAR_TYPES.ERROR })
          }
        })
        .catch(() => {
          setShowProgress(false)
          setShowToast({ show: true, text: t("failed"), type: SNACKBAR_TYPES.ERROR })
        })
    } else {
      setShowProgress(true)
      BookLibraryService.createBook(accessToken, {
        imageUrl: image,
        title: title,
        bookUrl: url,
        isPublic: true,
      })
        .then((res) => {
          setShowProgress(false)
          if (!checkLoginStatus(res)) {
            return
          }

          if (res.status === RESPONSE_CODE.CREATE_SUCCESS) {
            setShowToast({ show: true, text: t("success"), type: SNACKBAR_TYPES.SUCCESS })
            handleBack()
          } else {
            setShowToast({ show: true, text: t("failed"), type: SNACKBAR_TYPES.ERROR })
          }
        })
        .catch(() => {
          setShowProgress(false)
          setShowToast({ show: true, text: t("failed"), type: SNACKBAR_TYPES.ERROR })
        })
    }
  }

  const handleDelete = async () => {
    setShowProgress(true)
    BookLibraryService.deleteBook(accessToken, item?.id!)
      .then((res) => {
        setShowProgress(false)
        if (!checkLoginStatus(res)) {
          return
        }

        if (res.status === RESPONSE_CODE.SUCCESS) {
          setShowToast({ show: true, text: t("success"), type: SNACKBAR_TYPES.SUCCESS })
          handleBack()
        } else {
          setShowToast({ show: true, text: t("failed"), type: SNACKBAR_TYPES.ERROR })
        }
      })
      .catch(() => {
        setShowProgress(false)
        setShowToast({ show: true, text: t("failed"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  let headers: any[] = [
    {
      size: isPC ? 2 : isTablet ? 2 : 2,
      name: t("date"),
      sortby: null,
    },
    {
      size: isPC ? 2 : isTablet ? 2 : 2,
      name: t("image"),
      sortby: null,
    },
    {
      size: isPC ? 4 : isTablet ? 4 : 4,
      name: t("title"),
      sortby: null,
    },
    {
      size: isPC ? 4 : isTablet ? 2 : 4,
      name: t("link"),
      sortby: null,
    },
  ]

  return (
    <Box
      sx={{
        ...(isTablet
          ? { height: "auto", pt: "0px", pl: "20px" }
          : { width: "100%", pl: "20px", pr: "20px", position: "absolute", top: 60, bottom: 120 }),
        overflowY: "scroll",
      }}
    >
      {edit ? (
        <>
          <Typography.FileFieldDetail
            sx={{
              fontFamily: "Noto Sans JP !important",
              my: "12px",
              ml: "0px",
              ":hover": { opacity: 0.8, cursor: "pointer" },
            }}
            onClick={() => {
              closeDetail()
            }}
          >
            {"＜　"}
            {t("page_back")}
          </Typography.FileFieldDetail>

          {isTablet ? (
            <>
              <Grid
                container
                sx={{ mt: "0px", height: "32px", borderBottom: "1px dotted #D4D3D7" }}
              >
                {headers.map((header, index) => (
                  <Grid
                    item
                    key={index}
                    className="notosans weight500"
                    color="text.primary"
                    md={header.size}
                    sm={12}
                    xs={12}
                    fontSize={12}
                    sx={{ my: "auto", px: "20px", ":hover": { opacity: 0.8, cursor: "pointer" } }}
                  >
                    <BooksTableHeaderItem name={header.name} sortby={header.sortby} />
                  </Grid>
                ))}
              </Grid>
              <BooksTableItem item={item!} handleSelected={() => {}} />
            </>
          ) : (
            <>
              <Box>{item?.title}</Box>
            </>
          )}
        </>
      ) : (
        <></>
      )}

      <Box display={isTablet ? "flex" : "block"}>
        <Box width="100%">
          <Box mt={edit ? "20px" : "40px"}>
            <FileSelectField
              required
              name={t("book_cover")}
              handleChange={onFileSelected}
              file={image}
              validationMessage={imageValidationMessage}
              labelStyle={{
                fontFamily: "Noto Sans JP !important",
                fontSize: "16px",
                fontWeight: "700",
              }}
            />
          </Box>

          <InputTextField
            name={t("book_title")}
            required
            text={title}
            handleText={setTitle}
            sx={{ mt: "20px", mr: "10px" }}
            placeholder=""
            labelStyle={{ fontWeight: "700" }}
            inputStyle={{ width: isTablet ? "70%" : "100%" }}
            validationMessage={titleValidationMessage}
          />
          <InputTextField
            name={t("book_URL")}
            required
            text={url}
            handleText={setUrl}
            sx={{ mt: "20px", mr: "10px" }}
            placeholder=""
            labelStyle={{ fontWeight: "700" }}
            inputStyle={{ width: isTablet ? "70%" : "100%" }}
            validationMessage={urlValidationMessage}
          />
        </Box>
      </Box>
      <Box textAlign="center" sx={{ mt: "30px", mb: "30px" }}>
        {edit && (
          <Button
            variant="outlined"
            onClick={() => handleDelete()}
            sx={{
              my: "5px",
              mx: "0.5rem",
              width: 240,
              height: 48,
              fontSize: 16,
              borderRadius: 0,
              borderColor: "#FC3317",
              color: "#FC3317",
            }}
          >
            {t("delete")}
          </Button>
        )}
        <Button
          variant="contained"
          className="button_contact_us"
          onClick={() => handleSave()}
          sx={{
            ":hover": {
              bgcolor: "#2DA389",
            },
          }}
        >
          {t("save")}
        </Button>
      </Box>
    </Box>
  )
}
